import React from 'react';
import EditableAmountCell from './EditableAmountCell';
import { AdminResellerStatsData, ResellerType } from './AdminResellerStats';
import { getSalesStatus, getSalesStatusBadge } from './utils/salesStatusUtils';

interface AdminResellerStatsRowProps {
  stats: AdminResellerStatsData;
  commissionSettings: {
    totalCommission: number;
    supportCommissionToMainAssociation: number;
    supportCommissionToAssociation: number;
  };
  onPaidOutUpdate: (resellerId: string, resellerType: ResellerType, amount: number) => void;
}

const AdminResellerStatsRow: React.FC<AdminResellerStatsRowProps> = ({ stats, commissionSettings, onPaidOutUpdate }) => {
  const salesStatus = getSalesStatus(stats.salesStartDate, stats.salesEndDate, stats.paidOut, stats.earningsToCompare);
  const canEdit = stats.resellerType !== ResellerType.TeamMember;
  const resellerLabel = stats.resellerType === ResellerType.Association ? 'föreningen' : stats.resellerType === ResellerType.Team ? 'laget' : 'lagmedlemmen';

  const totalCommission = commissionSettings.totalCommission || 0;
  const supportCommissionToMainAssociation = commissionSettings.supportCommissionToMainAssociation || 0;
  const supportCommissionToAssociation = commissionSettings.supportCommissionToAssociation || 0;

  const pricePerUnit = stats.isMainAssociation ? totalCommission : (totalCommission - supportCommissionToMainAssociation!);
  const supportPricePerUnit = stats.isMainAssociation ? (totalCommission - supportCommissionToAssociation) : supportCommissionToAssociation;

  const mainHelpText = stats.isMainAssociation ? 'köp utan vald stödförening' : `köp av ${resellerLabel}`;
  const supportHelpText = stats.isMainAssociation ? 'köp med vald stödförening' : 'stödandelar vid köp av huvudförening';

  return (
    <div className="table-row" key={stats.resellerId}>
      <span>
        <strong>{stats.name}</strong>

        <small>{stats.sold} st (á {pricePerUnit}:-) {mainHelpText}</small>
        {stats.resellerType === ResellerType.Association && (
          <small>{stats.supportSold} st (á {supportPricePerUnit}:-) {supportHelpText}</small>
        )}
      </span>
      <span>
        <div className='flex flex-col'>
          {stats.email && <small>E-post: {stats.email}</small>}
          {stats.phone && <small>Mobil: {stats.phone}</small>}
          {stats.clearingNumber && <small>Clearingnr.: {stats.clearingNumber}</small>}
          {stats.accountNumber && <small>Kontonr.: {stats.accountNumber}</small>}
          {stats.accountOwner && <small>Kontoägare: {stats.accountOwner}</small>}
          {!stats.email && !stats.phone && !stats.clearingNumber && !stats.accountNumber && !stats.accountOwner && '-'}
        </div>
      </span>
      <span>
        {stats.salesPeriod && (
          <div className='flex flex-col gap-1'>
            {stats.salesPeriod}
            {getSalesStatusBadge(salesStatus, stats.paidOut, stats.earningsToCompare)}
          </div>
        )}
        {!stats.salesPeriod && '-'}
      </span>
      <span>
        {stats.isMainAssociation ? (
          <div className='flex flex-col'>-</div>
        ) : (
          <div className='flex flex-col'>
            {stats.supportEarnings > 0 && (
              <small className='inactive'>{stats.afEarnings - stats.supportEarnings} + {stats.supportEarnings} =</small>
            )}
            {stats.afEarnings} kr
          </div>
        )}
      </span>
      <span>
        <div className='flex flex-col'>
          {stats.isMainAssociation && (
            <small className='inactive'>{totalCommission * stats.sold} + {(totalCommission - supportCommissionToAssociation) * stats.supportSold} =</small>
          )}
          {stats.yourEarnings} kr
        </div>
      </span>
      <span>
        {canEdit ? (
          <EditableAmountCell
            value={stats.paidOut}
            onSave={(amount) => onPaidOutUpdate(stats.resellerId, stats.resellerType, amount)}
          />
        ) : null}
      </span>
    </div>
  );
};

export default AdminResellerStatsRow;

